import * as React from 'react';
import './Admin.less';
import { Form, Input, Button, Select, Divider, message, Spin, Checkbox, Tooltip, Row, Col, Modal, Alert } from 'antd';
import { FormComponentProps } from 'antd/lib/form/Form';
import bindAllOfThis from "src/utils/BindThisHelper";
import Routes from 'src/core/Routes';
import { Translate, withLocalize, LocalizeContextProps } from 'react-localize-redux';
import { PageProps } from 'src/models/common/ComponentProps';

import ActivityQuestions from './ActivityQuestions';
import { ActivityQuestionsProps } from './ActivityQuestions';
import ActivityQuestionCategories from './ActivityQuestionCategories';
import { ActivityQuestionCategoriesProps } from './ActivityQuestionCategories';
import ActivityUpdateTranslationDTO from 'src/models/activity/ActivityUpdateTranslationDTO';
import UpdateActivityDTO from 'src/models/activity/UpdateActivityDTO';
import KeyValueDTO from 'src/models/generic/KeyValueDTO';
import CultureApiController from 'src/api/CultureApiController';
import ActivityApiController from 'src/api/ActivityApiController';
import TextArea from 'antd/lib/input/TextArea';
import ActivitySummaries, { ActivitySummariesProps } from './ActivitySummaries';
import Languages, { Language } from 'src/constants/Languages';


interface RouteParams {
    id: string;
}

interface AddEditActivityProps extends PageProps<RouteParams>, LocalizeContextProps { }

interface AddEditActivityState {
    loading: boolean;
    isSubmitting: boolean;
    currentActivity: UpdateActivityDTO;
    shouldRefreshTable: boolean;
    allCultures: Language[];
    selectedCultureId: number;
}

interface ActivityFormProps {
    allCultures: Language[];
    selectedCultureId: number;
    currentActivity: UpdateActivityDTO;
    translate: (id: any) => any;
    onChange: (changedFields: any[]) => void;
}

// Form
let ActivityForm = Form.create({
    onFieldsChange(props: ActivityFormProps, changedFields: any[]) {
        props.onChange(changedFields);
    }
})(class extends React.Component<ActivityFormProps & FormComponentProps> {
    constructor(props: ActivityFormProps & FormComponentProps) {
        super(props);
    }

    private generateOptionsForSelect(keyValues: KeyValueDTO<string, number>[]): JSX.Element[] {
        return keyValues.map(keyValue => <Select.Option value={keyValue.value} key={keyValue.key} title={keyValue.key}>{keyValue.key}</Select.Option>);
    }

    private generateLanguageOptions() {
        // Only available languages
        return Languages.All.filter(x => x.isAvailable)
            .map(x => <Select.Option key={`cultureIds${x.id}`} value={x.id}>{x.name} ({x.code})</Select.Option>);
    }

    private renderIntroTextTranslations(): JSX.Element[] {
        if (this.props.currentActivity) {
            let translations = this.props.currentActivity.localizedText;

            return translations.map((translation: ActivityUpdateTranslationDTO, index: number) => {
                let id = "introductionText[" + index + "].text";
                let culture = this.props.allCultures.find(culture => culture.id === translation.cultureId).code.toLowerCase();
                let translationId = "admin.addEditActivity.introductionText." + culture;

                return this.renderStandardFormItem(
                    translationId,
                    id,
                    translation.introductionText,
                    <TextArea rows={4} autoComplete="off" />,
                    this.props.selectedCultureId === translation.cultureId,
                    false
                );
            });
        }
    }

    private renderConclusionTextTranslation(): JSX.Element[] {
        if (this.props.currentActivity) {
            let translations = this.props.currentActivity.localizedText;

            return translations.map((translation: ActivityUpdateTranslationDTO, index: number) => {
                let id = "conclusionText[" + index + "].text";
                let culture = this.props.allCultures.find(culture => culture.id === translation.cultureId).code.toLowerCase();
                let translationId = "admin.addEditActivity.conclusionText." + culture;

                return this.renderStandardFormItem(
                    translationId,
                    id,
                    translation.conclusionText,
                    <TextArea rows={4} autoComplete="off" />,
                    this.props.selectedCultureId === translation.cultureId,
                    false
                );
            });
        }
    }

    private renderStandardFormItem(translationId: string, id: string, initialValue, inputType, visible: boolean = true, required: boolean = true) {
        let translatedLabel = this.props.translate(translationId);

        if (visible) {
            return (
                <Form.Item label={translatedLabel}>
                    {this.props.form.getFieldDecorator(id, {
                        initialValue: initialValue,
                        rules: [{ required: required, message: this.props.translate('admin.common.requiredRule') }]
                    })(inputType)}
                </Form.Item>
            );
        }
    }

    render() {
        return (
            <Row>
                <Col span={12}>
                    <Form className="admin-form">
                        {this.renderStandardFormItem(
                            "admin.addEditActivity.formItem1",
                            "title",
                            this.props.currentActivity.title,
                            <Input autoComplete="off" disabled />
                        )}

                        {this.renderStandardFormItem(
                            "admin.addEditActivity.formItem2",
                            "selectedCultureId",
                            this.props.selectedCultureId,
                            <Select>{this.generateLanguageOptions()}</Select>
                        )}

                        {this.renderIntroTextTranslations()}
                        {this.renderConclusionTextTranslation()}
                    </Form>
                </Col>
            </Row>
        );
    }
});

class AddEditActivity extends React.Component<AddEditActivityProps, AddEditActivityState> {
    private formRef: React.Component<FormComponentProps>;

    constructor(props: AddEditActivityProps) {
        super(props);
        bindAllOfThis(this, AddEditActivity.prototype);

        let activityId = parseInt(this.props.match.params.id, 10);

        let currentActivity: UpdateActivityDTO = {
            activityId: activityId,
            title: "",
            isSurvey: false,
            localizedText: []
        };

        this.state = {
            loading: true,
            isSubmitting: false,
            shouldRefreshTable: false,
            allCultures: [],
            selectedCultureId: 0,
            currentActivity: currentActivity
        };
    }

    componentWillMount() {
        let cultures = Languages.All.filter((l) => l.isAvailable);
        this.setState({
            allCultures: cultures,
            selectedCultureId: cultures[0].id
        }, this.fillForm);
    }

    private fillForm(): void {
        ActivityApiController.getActivity(this.state.currentActivity.activityId).then((result) => {
            this.setState({
                loading: false,
                currentActivity: result.data
            });

            if (result.data.localizedText.length === 0) {
                let translations = this.state.allCultures.map(
                    (culture: Language): ActivityUpdateTranslationDTO => {
                        let translation: ActivityUpdateTranslationDTO = {
                            cultureId: culture.id,
                            introductionText: null,
                            conclusionText: null
                        };

                        return translation;
                    }
                );

                this.setState({
                    currentActivity: { ...this.state.currentActivity, ...{ localizedText: translations } }
                });
            }

        }).catch(e => {
            message.error(this.props.translate('admin.addEditActivity.messageRetrieveError'));
        });
    }

    private introductionTranslationsAreCompleted(): boolean {
        let localizedText = this.state.currentActivity.localizedText;
        let emptyTranslations = localizedText.filter(translation =>
            translation.introductionText === null || translation.introductionText === ""
        );

        return emptyTranslations.length === 0;
    }

    /**
     * Conclusions are not required.
     * Therefore, they are considered completed if either none of the translations are empty, or all of them are empty
     */
    private conclusionTranslationsAreCompleted(): boolean {
        let localizedText = this.state.currentActivity.localizedText;
        let emptyConclusions = localizedText.filter(translation =>
            translation.conclusionText === null || translation.conclusionText === "",
        );

        return emptyConclusions.length === 0 || emptyConclusions.length === localizedText.length;
    }

    private handleInputChange(changedFields: any[]): void {
        let newState = {};
        for (var key in changedFields) {
            if (changedFields.hasOwnProperty(key)) {
                let field: any = changedFields[key];

                // If key is the nested property
                if (key === 'introductionText' || key === 'conclusionText') {
                    let fieldKey = '';
                    let localizedTextKey = 'localizedText';
                    for (var fieldName in field) {
                        if (field.hasOwnProperty(fieldName)) {
                            fieldKey = fieldName;
                            break;
                        }
                    }

                    newState[localizedTextKey] = [...this.state.currentActivity.localizedText];
                    newState[localizedTextKey][fieldKey][key] = field[fieldKey].text.value;
                } else if (key === 'selectedCultureId') {
                    this.setState({ selectedCultureId: field.value });
                    return;
                } else {
                    newState[key] = field.value;
                }
            }
        }
        this.setState({
            currentActivity: { ...this.state.currentActivity, ...newState }
        });
    }

    private updateActivity(): void {
        ActivityApiController.updateActivity(this.state.currentActivity)
            .then(result => {
                message.success(this.props.translate('admin.addEditActivity.messageUpdate'));
                this.setState({ isSubmitting: false });
            }).catch(e => {
                message.error(this.props.translate('admin.addEditActivity.messageUpdateError'));
                this.setState({ isSubmitting: false });
            });
    }

    private handleSubmit(e): void {
        e.preventDefault();

        this.formRef.props.form.validateFields((error: any, values: any) => {
            if (!error) {
                this.setState({ isSubmitting: true });
                this.updateActivity();
            }
        });
    }

    private handleCancel(e): void {
        this.props.history.push(Routes.ADMIN_ACTIVITIES);
    }

    private updateShouldRefreshTable(): void {
        this.setState({ shouldRefreshTable: !this.state.shouldRefreshTable });
    }

    private renderSaveButton(): JSX.Element {
        //if (!this.introductionTranslationsAreCompleted()) {
        //    return (
        //        <Tooltip placement="bottom" title={this.props.translate('admin.addEditActivity.saveTooltip')} arrowPointAtCenter>
        //            <Button type="primary" disabled>
        //                {this.props.translate("admin.common.saveButton")}
        //            </Button>
        //        </Tooltip>
        //    );
        //}
        //else if (!this.conclusionTranslationsAreCompleted()) {
        //    return (
        //        <Tooltip placement="bottom" title={this.props.translate('admin.addEditActivity.conclusionsTooltip')} arrowPointAtCenter>
        //            <Button type="primary" disabled>
        //                {this.props.translate("admin.common.saveButton")}
        //            </Button>
        //        </Tooltip>
        //    );
        //}
        //else {
        //    return (
        //        <Button type="primary" onClick={this.handleSubmit} loading={this.state.isSubmitting}>
        //            {this.props.translate("admin.common.saveButton")}
        //        </Button>
        //    );
        //}
        return (
            <Button type="primary" onClick={this.handleSubmit} loading={this.state.isSubmitting}>
                {this.props.translate("admin.common.saveButton")}
            </Button>
        );
    }

    private renderActivityQuestionCategories(): JSX.Element {
        let categoriesTableProps = {
            activityId: this.state.currentActivity.activityId,
            updateShouldRefreshTable: this.updateShouldRefreshTable
        } as ActivityQuestionCategoriesProps;

        if (!this.state.currentActivity.isSurvey) {
            return (
                <div>
                    <br /> <br />
                    <ActivityQuestionCategories {...categoriesTableProps} />
                    <br /> <br />
                </div>
            );
        }
    }

    render() {
        if (this.state.loading) {
            return <Spin />;
        }

        let formProps: ActivityFormProps = {
            allCultures: this.state.allCultures,
            selectedCultureId: this.state.selectedCultureId,
            currentActivity: this.state.currentActivity,
            translate: this.props.translate,
            onChange: this.handleInputChange
        };

        let questionsTableProps = {
            activityId: this.state.currentActivity.activityId,
            isSurvey: this.state.currentActivity.isSurvey,
            shouldRefreshTable: this.state.shouldRefreshTable,
            updateShouldRefreshTable: this.updateShouldRefreshTable
        } as ActivityQuestionsProps;


        let summaryTableProps = {
            activityId: this.state.currentActivity.activityId,
            shouldRefreshTable: this.state.shouldRefreshTable,
            updateShouldRefreshTable: this.updateShouldRefreshTable,
        } as ActivitySummariesProps;

        return (
            <div>
                <h2>
                    {this.state.currentActivity.title}
                </h2>

                <ActivityForm wrappedComponentRef={ref => this.formRef = ref} {...formProps} />

                <div>
                    {this.renderSaveButton()}
                    <Divider type="vertical" />
                    <Button onClick={this.handleCancel}>
                        {this.props.translate("common.back")}
                    </Button>
                </div>
                <br /><br />
                <ActivityQuestions {...questionsTableProps} />
                {this.renderActivityQuestionCategories()}
                <ActivitySummaries {...summaryTableProps} />
            </div>
        );
    }
}

export default withLocalize(AddEditActivity);



// WEBPACK FOOTER //
// ./src/pages/admin/AddEditActivity.tsx